/**
 * RWBR — THE SEASON'S STORY.
 *
 * Race Week generates about 2,766 events a season — feuds started, interviews
 * given, crew chiefs quitting, a driver killed in qualifying — and until this
 * module existed every one of them was written to the ledger and read by
 * nobody. The engine was telling a story into an empty room.
 *
 * WHAT THIS IS NOT. It is not a narrator. It writes no prose, invents no
 * sentence, and adds no adjective. Every line it returns is a `description`
 * string the rules engine already produced, carried with the citation that
 * produced it. This module only decides what is WORTH SHOWING and in what
 * order — which is a different job from writing, and the only one that can be
 * done without risking a fact nobody rolled for.
 *
 * WHY RANK AT ALL. A week emits ~77 events, of which about 33 are "Bo Ficklin
 * (no trophy) goes to the bubble stack." Printing them in sequence buries the
 * driver who died on Saturday under sixty placements. So each moment tag
 * carries a WEIGHT — how rare and how consequential the thing is — and the
 * digest leads with the heaviest. Rarity is not guessed: the TV EVENT Z chart
 * is documented as "ONE event from this list should occur every racing season",
 * TV EVENT Y as "every third or fourth race", TV EVENT X as "approximately
 * every race", and the weights below follow that stated frequency.
 *
 * THE HEADLINE RULE. A season headline must be something that would still be
 * talked about in March. Placements, counts and grids never qualify no matter
 * how the week went; deaths, season-ending injuries, suspensions, sackings and
 * Z-chart readings always do.
 */

import type { LedgerStream } from './persistence.ts';

/**
 * The shape both sources reduce to. A live season carries `SeasonEvent`s and a
 * saved one carries `LedgerRow`s; they differ only in casing and in which
 * fields survived the trip, so both are normalised here rather than teaching
 * the interface to hold two shapes.
 */
export interface StoryEvent {
  stream: LedgerStream;
  raceNumber: number | null;
  seq: number;
  momentTag: string;
  ruleCitation: string;
  description: string;
  actorIds: string[];
}

export const fromLedgerRows = (rows: readonly {
  stream: LedgerStream;
  race_number: number | null;
  seq: number;
  moment_tag: string;
  rule_citation: string;
  description: string;
  actor_ids: string[];
}[]): StoryEvent[] =>
  rows.map((r) => ({
    stream: r.stream,
    raceNumber: r.race_number,
    seq: r.seq,
    momentTag: r.moment_tag,
    ruleCitation: r.rule_citation,
    description: r.description,
    actorIds: r.actor_ids ?? [],
  }));

export const fromSeasonEvents = (events: readonly {
  phase?: string;
  raceNumber: number;
  sequence: number;
  momentTag: string;
  ruleCitation: string;
  description: string;
  actorIds: string[];
}[]): StoryEvent[] =>
  events.map((e) => ({
    stream: (e.momentTag.startsWith('raceweek.') ? 'race-week' : 'season') as LedgerStream,
    raceNumber: e.raceNumber,
    seq: e.sequence,
    momentTag: e.momentTag,
    ruleCitation: e.ruleCitation,
    description: e.description,
    actorIds: e.actorIds ?? [],
  }));

// ---------------------------------------------------------------------------
// What matters, and how much
// ---------------------------------------------------------------------------

/**
 * Weight by moment tag. Higher is bigger news.
 *
 * The bands, and why:
 *
 *   100+  Irreversible. A man is dead, out for the year, or out of a ride.
 *    80+  Z chart — "ONE event from this list should occur every racing
 *         season" (RWEG/p.10). By the booklet's own frequency, seeing one at
 *         all is the story of the week.
 *    60+  Suspensions, sackings, missed races: he is not on the grid.
 *    40+  Y chart — "every third or fourth race" (RWEG/p.9). Feuds start here.
 *    25+  Money and points changing hands; the pole.
 *    10+  X chart — "approximately every race" (RWEG/p.8), interviews.
 *      1  Placements, counts, the grid: the week's plumbing.
 *
 * A tag absent from this table gets `DEFAULT_WEIGHT`, so a new rule module
 * appears in the digest at ordinary priority rather than vanishing from it.
 */
export const WEIGHTS: Readonly<Record<string, number>> = {
  'raceweek.death': 120,
  'raceweek.unable.dead': 120,
  'raceweek.unable.season-injury': 110,
  'raceweek.unable.quit': 100,
  'raceweek.transfer': 95,
  'raceweek.tv-event-z': 85,
  'raceweek.unable.injury': 70,
  'raceweek.unable.arrested': 70,
  'raceweek.unable.car-destroyed': 68,
  'raceweek.suspension': 65,
  'raceweek.unable.suspended': 65,
  'raceweek.injury': 62,
  'raceweek.team-withdrew': 60,
  'raceweek.feud': 45,
  'raceweek.tv-event-y': 42,
  'raceweek.fine': 30,
  'raceweek.points-penalty': 30,
  'raceweek.placement.pole': 28,
  'raceweek.chip-forfeit': 22,
  'raceweek.fume-list': 20,
  'raceweek.tv-event-x': 16,
  'raceweek.tv-interviews': 14,
  'raceweek.tv-list.extra-draw': 12,
  'raceweek.race-week-tv-list': 10,
  'raceweek.dnq': 8,
  'raceweek.tv-list.gold': 6,
  'raceweek.tv-list.carry-over': 4,
  'raceweek.tv-list.count': 3,
  'raceweek.grid': 3,
  'raceweek.round-out': 2,
  'raceweek.open-spaces': 2,
  'raceweek.setup': 1,
  'raceweek.bubble.did-not-race': 1,
  'raceweek.fast-week.count': 1,
  'raceweek.slow-week.count': 1,
  'raceweek.uneventful.count': 1,
  'raceweek.placement.a': 1,
  'raceweek.placement.b': 1,
  'raceweek.placement.c': 1,
  'raceweek.placement.d': 1,
  'raceweek.fast-week.place': 1,
  'raceweek.slow-week.place': 1,
  'raceweek.uneventful-week.place': 1,
};

export const DEFAULT_WEIGHT = 12;

export const weigh = (momentTag: string): number => WEIGHTS[momentTag] ?? DEFAULT_WEIGHT;

/**
 * Would this still be talked about in March?
 *
 * Deliberately a floor on weight rather than a list, so a rule module added
 * later is judged by the same standard as everything else instead of being
 * silently excluded for not being on a list somebody forgot to update.
 */
export const HEADLINE_FLOOR = 40;

export const isHeadline = (e: StoryEvent): boolean => weigh(e.momentTag) >= HEADLINE_FLOOR;

// ---------------------------------------------------------------------------
// One week
// ---------------------------------------------------------------------------

export interface WeekStory {
  raceNumber: number;
  /** Ranked, heaviest first, ties broken by the order they happened. */
  moments: StoryEvent[];
  /** The subset worth a headline. May be empty — most weeks are quiet. */
  headlines: StoryEvent[];
  /** Verbatim quotes, kept apart because they read as speech, not report. */
  interviews: StoryEvent[];
  /** The one-line summary the engine already wrote for the grid, if present. */
  grid: StoryEvent | null;
  /** Everything, in the order it happened, for anyone who wants the lot. */
  all: StoryEvent[];
  totalEvents: number;
}

const byWeightThenOrder = (a: StoryEvent, b: StoryEvent): number =>
  weigh(b.momentTag) - weigh(a.momentTag) || a.seq - b.seq;

/**
 * Build one week's story.
 *
 * `momentLimit` trims the ranked list for display. It is a DISPLAY limit only —
 * `all` keeps every event, and `totalEvents` reports the true count, so a
 * screen can say "and 61 more" rather than quietly implying that was all that
 * happened.
 */
export function weekStory(
  events: readonly StoryEvent[],
  raceNumber: number,
  momentLimit = 12,
): WeekStory {
  const all = events
    .filter((e) => e.raceNumber === raceNumber && e.stream === 'race-week')
    .sort((a, b) => a.seq - b.seq);

  const interviews = all.filter((e) => e.momentTag === 'raceweek.tv-interviews');
  const ranked = all
    .filter((e) => e.momentTag !== 'raceweek.tv-interviews')
    .sort(byWeightThenOrder);

  return {
    raceNumber,
    moments: ranked.slice(0, momentLimit),
    headlines: all.filter(isHeadline).sort(byWeightThenOrder),
    interviews,
    grid: all.find((e) => e.momentTag === 'raceweek.grid') ?? null,
    all,
    totalEvents: all.length,
  };
}

/** Every week that produced anything, in order. */
export function seasonWeeks(events: readonly StoryEvent[], momentLimit = 12): WeekStory[] {
  const numbers = [
    ...new Set(
      events.filter((e) => e.stream === 'race-week' && e.raceNumber !== null).map((e) => e.raceNumber as number),
    ),
  ].sort((a, b) => a - b);
  return numbers.map((n) => weekStory(events, n, momentLimit));
}

// ---------------------------------------------------------------------------
// The season, in headlines
// ---------------------------------------------------------------------------

export interface Headline extends StoryEvent {
  weight: number;
}

/**
 * The year's biggest moments, heaviest first.
 *
 * Ties break on the EARLIER event, not the later one: when a season contains
 * two equally weighted disasters, the one that happened first is the one the
 * rest of the year was shaped by.
 */
export function seasonHeadlines(events: readonly StoryEvent[], limit = 20): Headline[] {
  return events
    .filter((e) => e.stream === 'race-week' && isHeadline(e))
    .map((e) => ({ ...e, weight: weigh(e.momentTag) }))
    .sort((a, b) => b.weight - a.weight || (a.raceNumber ?? 0) - (b.raceNumber ?? 0) || a.seq - b.seq)
    .slice(0, limit);
}

/**
 * How loud the season was, by category. Counts only — no interpretation.
 *
 * `quietWeeks` counts weeks that produced NO headline at all, which is the
 * honest majority and worth showing: a season where every week is dramatic is
 * a season nobody would believe.
 *
 * WHAT `xEvents` / `yEvents` / `zEvents` ACTUALLY COUNT — readings that ENDED
 * on that chart, not times the chart was consulted. The two differ, because the
 * charts chain: a roll of 11 on the TV LIST goes to TV EVENT X, an 11 there
 * goes on to Y, and an 11 there to Z. The engine emits its event for the chart
 * that finally SPOKE, so a chain ending on Z is counted once, under Z, and not
 * also under X and Y along the way.
 *
 * Measured over three seasons, the charts were consulted 693 / 128 / 29 / 4
 * times against 464 / 99 / 25 / 4 terminal readings — so reading these counts
 * as consultations would understate X by about a quarter. The screen says
 * "ended on" for exactly this reason.
 */
export interface SeasonTone {
  weeks: number;
  quietWeeks: number;
  feuds: number;
  interviews: number;
  zEvents: number;
  yEvents: number;
  xEvents: number;
  suspensions: number;
  injuries: number;
  deaths: number;
  dnqWeeks: number;
  chipForfeits: number;
}

export function seasonTone(events: readonly StoryEvent[]): SeasonTone {
  const week = events.filter((e) => e.stream === 'race-week');
  const count = (tag: string): number => week.filter((e) => e.momentTag === tag).length;
  const weeks = new Set(week.map((e) => e.raceNumber)).size;
  const loud = new Set(week.filter(isHeadline).map((e) => e.raceNumber)).size;

  return {
    weeks,
    quietWeeks: weeks - loud,
    feuds: count('raceweek.feud'),
    interviews: count('raceweek.tv-interviews'),
    zEvents: count('raceweek.tv-event-z'),
    yEvents: count('raceweek.tv-event-y'),
    xEvents: count('raceweek.tv-event-x'),
    suspensions: count('raceweek.suspension') + count('raceweek.unable.suspended'),
    injuries:
      count('raceweek.injury') + count('raceweek.unable.injury') + count('raceweek.unable.season-injury'),
    deaths: count('raceweek.death') + count('raceweek.unable.dead'),
    dnqWeeks: count('raceweek.dnq'),
    chipForfeits: count('raceweek.chip-forfeit'),
  };
}

/**
 * Every week a named driver appears in, with his moments.
 *
 * This is the per-driver story thread — what the season did to one man. Weeks
 * he had nothing to do with are dropped entirely rather than listed empty.
 */
export function driverThread(events: readonly StoryEvent[], driverId: string): WeekStory[] {
  const his = events.filter((e) => e.stream === 'race-week' && e.actorIds.includes(driverId));
  const numbers = [...new Set(his.map((e) => e.raceNumber as number))].sort((a, b) => a - b);
  return numbers.map((n) => {
    const all = his.filter((e) => e.raceNumber === n).sort((a, b) => a.seq - b.seq);
    return {
      raceNumber: n,
      moments: [...all].sort(byWeightThenOrder),
      headlines: all.filter(isHeadline).sort(byWeightThenOrder),
      interviews: all.filter((e) => e.momentTag === 'raceweek.tv-interviews'),
      grid: null,
      all,
      totalEvents: all.length,
    };
  });
}
